import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DrillCone, DrillSpec, DrillArrow } from '@4cm/core/drills/types';
import { supabase } from '../../lib/supabase';
import { BrainOrb, BrainPrompt } from '../primitives';
import { DraftCard } from '../DraftCard';
import { useBrain } from '../../lib/brain';
import { useVoiceToText } from '../../lib/voiceInput';
import { useBrainAutoSpeak } from '../../lib/useBrainAutoSpeak';
import { drillSpecToTacticsLayout } from '../../lib/drillTacticsAdapter';
import { loadTacticsLayout, saveTacticsLayout } from '../../lib/tactics3dStore';
import { glassPanel } from './liquidGlass';
import type { PitchView, CustomPitch } from './pitchArea';
import type { DrillRolePlayer } from './playerRoles';

interface DrillBrainPanelProps {
  drillId: string;
  teamId: string | null | undefined;
  /** TacticsLayout localStorage key — used to apply Brain spots in-place. */
  persistKey: string;
  /** Called with the spec's cones so the parent's equipment overlay
   *  picks up Brain-drafted equipment. */
  onApplyCones: (cones: DrillCone[]) => void;
  /** Wave 4.6 — apply role players from Brain setup. Additive merge. */
  onApplyRolePlayers?: (players: DrillRolePlayer[]) => void;
  /** Wave 4.6 — set the pitch working area from Brain setup. */
  onApplyPitchView?: (view: PitchView, custom: CustomPitch | null) => void;
  /** Wave 4.6 — set the coach's target player count. */
  onApplyTargetCount?: (n: number) => void;
  /** Append Brain-drafted arrows to the canvas. */
  onApplyArrows?: (arrows: DrillArrow[]) => void;
  /** Replace the canvas's keyframe set with Brain's drafted animation. */
  onApplyKeyframes?: (keyframes: import('@4cm/core/drills/types').DrillKeyframe[]) => void;
  /** Latest authored spec — supplies refine context so Brain knows what
   *  the coach is iterating on. */
  currentSpec: DrillSpec | null;
}

/**
 * Drill-scoped Brain panel for the drill canvas.
 *
 *   - screen.kind = 'drills' so Brain emits drill drafts (not tactics)
 *   - Apply on a draft_drill writes spec.entities into the live
 *     TacticsLayout (so the board re-renders) and spec.cones up to the
 *     parent's equipment overlay
 *   - Other draft types use the global-chat-style disabled state with
 *     a friendly hint
 *   - Voice + auto-speak parity with the tactics panel
 */
export function DrillBrainPanel({
  drillId,
  teamId,
  persistKey,
  onApplyCones,
  onApplyRolePlayers,
  onApplyPitchView,
  onApplyTargetCount,
  onApplyArrows,
  onApplyKeyframes,
  currentSpec,
}: DrillBrainPanelProps): React.JSX.Element {
  const screen = useMemo(
    () => ({
      kind: 'drills' as const,
      drillId,
      teamId: teamId ?? null,
      // Pass the current spec so Brain knows what's already on the
      // canvas when the coach asks for a refinement ("move the server
      // out wide", "add another defender").
      drill_spec: currentSpec ?? undefined,
    }),
    [drillId, teamId, currentSpec],
  );

  const brain = useBrain({ teamId: teamId ?? null, screen });
  const [approving, setApproving] = useState<string | null>(null);

  // Other surfaces on the drill canvas (notes/clip panel, form-field
  // AI assists) can ask Brain a question by dispatching a
  // 'drill-brain:send' window event with { prompt }. We listen +
  // forward to brain.send so the conversation stays in this panel.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ prompt?: string }>;
      const prompt = ce.detail?.prompt;
      if (prompt) brain.send(prompt);
    };
    window.addEventListener('drill-brain:send', handler);
    return () => window.removeEventListener('drill-brain:send', handler);
  }, [brain]);

  const voice = useVoiceToText({
    onTranscript: (text) => brain.send(text),
  });
  useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });

  // Auto-scroll to the bottom of the chat as Brain streams its reply.
  // The dependency list must include the LAST TURN'S TEXT so we re-fire
  // on every incremental token chunk — depending only on turns.length
  // misses streams because the assistant turn already exists from token
  // 1 onward.
  const historyRef = useRef<HTMLUListElement>(null);
  const lastTurnText = brain.turns[brain.turns.length - 1]?.text ?? '';
  useEffect(() => {
    const el = historyRef.current;
    if (!el) return;
    // Use instant 'auto' during streaming — smooth-scroll fights with
    // rapid token updates. requestAnimationFrame lands the scroll
    // after the DOM commits the new text.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [brain.turns.length, brain.loading, lastTurnText]);

  const applyDraftDrill = useCallback((spec: DrillSpec) => {
    // Entities → TacticsLayout spots. Merge into the EXISTING layout so
    // we don't blow away players the coach already placed manually.
    const existing = loadTacticsLayout(persistKey) ?? {
      homeFormation: '4-3-3',
      awayFormation: '4-3-3',
      homeSpots: [],
      awaySpots: [],
      arrows: [],
    };
    const layoutFromSpec = drillSpecToTacticsLayout(spec);
    const merged = {
      ...existing,
      homeSpots: [...existing.homeSpots, ...layoutFromSpec.homeSpots],
      awaySpots: [...existing.awaySpots, ...layoutFromSpec.awaySpots],
      arrows: [...existing.arrows, ...layoutFromSpec.arrows],
    };
    saveTacticsLayout(persistKey, merged);

    // Cones → overlay state (additive merge so coach manual placements
    // survive Brain applies).
    if (spec.cones && spec.cones.length > 0) {
      // Re-id incoming cones so they don't clash with whatever's already
      // on the overlay.
      const stamped = spec.cones.map((c) => ({
        ...c,
        id: c.id || `eq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      }));
      onApplyCones(stamped);
    }
  }, [persistKey, onApplyCones]);

  // Brain occasionally emits positions in pitch metres (0..68 / 0..105)
  // instead of the normalised [0..1] coord system the canvas expects.
  // Defensive normaliser: if either axis looks > 1, treat as metres
  // and divide by the pitch dims. Clamps to [0, 1] so a stray value
  // can never render off-canvas.
  const normalisePosition = useCallback((p: [number, number]): [number, number] => {
    const [x, y] = p;
    const PITCH_W_M = 68;
    const PITCH_L_M = 105;
    const looksLikeMetres = Math.abs(x) > 1.5 || Math.abs(y) > 1.5;
    const nx = looksLikeMetres ? x / PITCH_W_M : x;
    const ny = looksLikeMetres ? y / PITCH_L_M : y;
    return [clamp01(nx), clamp01(ny)];
  }, []);

  const applyDraftSetup = useCallback((payload: Record<string, unknown>) => {
    // Pitch view + custom dims. Brain occasionally uses 'half-attacking'
    // (hyphen) instead of 'half_attacking' (underscore). Normalise so
    // the overlay's switch still resolves.
    const rawView = payload.pitch_view as string | undefined;
    const view = (rawView ? rawView.replace(/-/g, '_').toLowerCase() : undefined) as PitchView | undefined;
    if (view && onApplyPitchView) {
      const custom = payload.custom_pitch as { width: number; length: number } | undefined;
      const customForState: CustomPitch | null = custom
        ? { width: custom.width, length: custom.length, unit: 'm' }
        : null;
      onApplyPitchView(view, view === 'custom' ? customForState : null);
    }
    // Target count.
    const target = payload.target_player_count as number | undefined;
    if (typeof target === 'number' && onApplyTargetCount) onApplyTargetCount(target);
    // ---- ID generation upfront (shared by players + keyframes) ----
    // Brain references players by label (e.g. "A1"). We mint stable IDs
    // ONCE here, stamp them on the placed players, AND use the same map
    // when resolving keyframe references. Without sharing, keyframes
    // point at IDs the player set doesn't have → animation no-ops.
    const incoming = payload.role_players as Array<{ role_id: string; position: [number, number]; label?: string }> | undefined;
    const ballPayload = payload.ball as { position: [number, number] } | undefined;
    const turnStamp = Date.now().toString(36);
    const rnd = () => Math.random().toString(36).slice(2, 5);

    const labelToId: Record<string, string> = {};
    const stampedPlayers: DrillRolePlayer[] = [];
    if (incoming) {
      incoming.forEach((p, i) => {
        const id = `p-${turnStamp}-${i}-${rnd()}`;
        if (p.label) labelToId[p.label] = id;
        // Normalise Brain's free-form role_id ("Defender", "ATTACKER")
        // to the slug the player roles config uses ("defender" etc.).
        // Unknown values fall through to 'attacker' to avoid hard fails.
        const roleId = (p.role_id ?? 'attacker').toString().toLowerCase().trim();
        stampedPlayers.push({
          id,
          roleId,
          position: normalisePosition(p.position),
          label: p.label,
        });
      });
    }
    const ballId = `p-${turnStamp}-ball-${rnd()}`;
    if (ballPayload?.position) {
      let pos = normalisePosition(ballPayload.position);
      const conflicts = incoming?.some((p) => {
        const pp = normalisePosition(p.position);
        return Math.hypot(pp[0] - pos[0], pp[1] - pos[1]) < 0.02;
      }) ?? false;
      if (conflicts) {
        pos = [Math.min(1, pos[0] + 0.05), Math.min(1, pos[1] + 0.05)];
      }
      stampedPlayers.push({ id: ballId, roleId: 'ball', position: pos });
    }
    if (stampedPlayers.length > 0 && onApplyRolePlayers) {
      onApplyRolePlayers(stampedPlayers);
    }

    // Equipment cones — same defensive normalisation.
    const cones = payload.cones as DrillCone[] | undefined;
    if (cones && cones.length > 0) {
      const stamped = cones.map((c, i) => ({
        ...c,
        id: c.id || `eq-${turnStamp}-${i}-${rnd()}`,
        position: normalisePosition(c.position),
      }));
      onApplyCones(stamped);
    }

    // Keyframes — multi-phase animation. We now share labelToId + ballId
    // with the placed players above, so animation references resolve.
    const incomingKfs = payload.keyframes as Array<{
      label?: string; durationSec: number;
      role_players?: Array<{ label: string; position: [number, number] }>;
      ball?: { position: [number, number] };
    }> | undefined;
    if (incomingKfs && incomingKfs.length >= 1 && onApplyKeyframes) {
      const out = incomingKfs.map((kf, idx) => {
        const rolePlayers: Record<string, [number, number]> = {};
        for (const rp of (kf.role_players ?? [])) {
          const id = labelToId[rp.label];
          if (id) rolePlayers[id] = normalisePosition(rp.position);
        }
        if (kf.ball?.position) {
          rolePlayers[ballId] = normalisePosition(kf.ball.position);
        }
        return {
          id: `kf-${turnStamp}-${idx}-${rnd()}`,
          label: kf.label ?? `Phase ${idx + 1}`,
          durationSec: idx === 0 ? 0 : Math.max(0.1, kf.durationSec ?? 1.5),
          positions: {},
          rolePlayers,
        };
      });
      onApplyKeyframes(out);
    }

    // Movement arrows — pass/run/dribble/press/cover. Empty list = no
    // change so a refine prompt doesn't clobber the coach's arrows.
    const incomingArrows = payload.arrows as Array<{ from: [number, number]; to: [number, number]; kind: DrillArrow['kind']; label?: string }> | undefined;
    if (incomingArrows && incomingArrows.length > 0 && onApplyArrows) {
      const arrowsToApply: DrillArrow[] = incomingArrows.map((a, i) => ({
        id: `arr-${Date.now().toString(36)}-${i}-${Math.random().toString(36).slice(2, 5)}`,
        from: { kind: 'point', position: normalisePosition(a.from) },
        to:   { kind: 'point', position: normalisePosition(a.to) },
        kind: a.kind,
        style: a.kind === 'pass' ? 'solid'
             : a.kind === 'run' ? 'dashed'
             : a.kind === 'dribble' ? 'solid'
             : a.kind === 'press' ? 'dashed'
             : a.kind === 'cover' ? 'dotted'
             : 'solid',
        curvature: (a.kind === 'dribble' ? 0.35 : a.kind === 'press' ? 0.15 : a.kind === 'cover' ? 0.2 : 0) || undefined,
        color: a.kind === 'pass' ? '#27AE60'
             : a.kind === 'run' ? '#F39C12'
             : a.kind === 'dribble' ? '#E67E22'
             : a.kind === 'press' ? '#C0392B'
             : a.kind === 'cover' ? '#2980B9'
             : '#27AE60',
        label: a.label,
      }));
      onApplyArrows(arrowsToApply);
    }
    // Persist Brain's drill metadata. Values are normalised first so
    // free-form strings ("intermediate", "Rondo", "In Possession") don't
    // crash the UPDATE on Postgres enum constraints.
    if (drillId) {
      const patch: Record<string, unknown> = {};
      const text = (k: string) => typeof payload[k] === 'string' ? (payload[k] as string).trim() : null;
      const arr = (k: string) => Array.isArray(payload[k]) ? (payload[k] as unknown[]).filter((x) => typeof x === 'string') as string[] : null;
      const num = (k: string) => typeof payload[k] === 'number' ? (payload[k] as number) : null;

      const name = text('name'); if (name) patch.name = name;
      const description = text('description'); if (description) patch.description = description;

      // drill_type enum: WARM_UP | TECHNICAL | TACTICAL | SMALL_SIDED_GAME | COOL_DOWN
      const drillTypeRaw = text('drill_type');
      const drillType = normaliseDrillType(drillTypeRaw);
      if (drillType) patch.drill_type = drillType;

      const duration = num('duration_mins'); if (duration) patch.duration_mins = duration;

      // difficulty enum: FOUNDATION | INTERMEDIATE | ADVANCED
      const diffRaw = text('difficulty');
      const difficulty = normaliseDifficulty(diffRaw);
      if (difficulty) patch.difficulty = difficulty;

      const minPlayers = num('min_players'); if (minPlayers) patch.min_players = minPlayers;
      const maxPlayers = num('max_players'); if (maxPlayers) patch.max_players = maxPlayers;
      const space = text('space_required'); if (space) patch.space_required = space;
      const equipment = arr('equipment'); if (equipment && equipment.length) patch.equipment = equipment;

      // corner_ids: only keep recognised corner-* slugs (Brain sometimes
      // emits free-form like "possession" — drop it rather than reject).
      const cornerIdsRaw = arr('corner_ids');
      const cornerIds = cornerIdsRaw?.map(normaliseCornerId).filter((x): x is string => !!x);
      if (cornerIds && cornerIds.length) patch.corner_ids = cornerIds;

      const primaryCorner = normaliseCornerId(text('primary_corner_id'));
      if (primaryCorner) patch.primary_corner_id = primaryCorner;

      const subThemes = arr('sub_themes'); if (subThemes && subThemes.length) patch.sub_themes = subThemes;

      // primary_phase enum: attacking | defending | transitions | individual
      const phaseRaw = text('primary_phase');
      const primaryPhase = normalisePhase(phaseRaw);
      if (primaryPhase) patch.primary_phase = primaryPhase;

      const progressions = arr('progressions'); if (progressions && progressions.length) patch.progressions = progressions;
      const regressions = arr('regressions'); if (regressions && regressions.length) patch.regressions = regressions;
      const organisation = text('organisation'); if (organisation) patch.organisation_md = organisation;
      const coachingPoints = arr('coaching_points'); if (coachingPoints && coachingPoints.length) patch.coaching_points = coachingPoints;

      if (Object.keys(patch).length > 0) {
        // Surface errors loudly — silent `void` swallows enum rejections
        // and leaves the coach with an empty card thinking it saved.
        supabase.from('drills').update(patch).eq('id', drillId).then(({ error }) => {
          if (error) {
            // eslint-disable-next-line no-console
            console.error('[drill apply] metadata update failed', error.message, patch);
            // eslint-disable-next-line no-alert
            alert(`Couldn't save Brain's drill details: ${error.message}`);
          }
        });
      }
    }
  }, [onApplyPitchView, onApplyTargetCount, onApplyRolePlayers, onApplyCones, onApplyArrows, onApplyKeyframes, normalisePosition, drillId]);

  const isApplyable = (type: string): boolean =>
    type === 'draft_drill' || type === 'draft_drill_setup';

  function clamp01(v: number): number {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  return (
    <div style={panelStyle} data-testid="drill-brain-panel">
      <header style={headerStyle}>
        {/* Larger orb that tracks voice state — matches the prominent
            BrainOrb in onboarding so the coach gets the same "Brain is
            listening" visual cue when voice-prompting. */}
        <BrainOrb
          size="md"
          ariaLabel="Brain"
          state={voice.listening ? 'listening' : brain.loading ? 'thinking' : 'idle'}
        />
        <div>
          <div style={eyebrowStyle}>Brain · drill designer</div>
          <h2 style={titleStyle}>Draft, refine, apply.</h2>
        </div>
      </header>

      {brain.turns.length === 0 ? (
        <Examples onPick={(text) => brain.send(text)} />
      ) : (
        <ul ref={historyRef} style={historyStyle} role="log" aria-live="polite">
          {brain.turns.map((turn) => (
            <TurnCard
              key={turn.id}
              turn={turn}
              approving={approving === turn.id}
              currentSpec={currentSpec}
              onApprove={async () => {
                if (approving || !turn.draft) return;
                setApproving(turn.id);
                try {
                  if (turn.draft.type === 'draft_drill') {
                    // Library-drill draft → apply its spec to canvas.
                    const p = turn.draft.payload as Record<string, unknown>;
                    const spec = p.drill_spec as DrillSpec | undefined;
                    if (spec) applyDraftDrill(spec);
                  } else if (turn.draft.type === 'draft_drill_setup') {
                    // Canvas-setup draft → apply pitch + roles + equipment.
                    applyDraftSetup(turn.draft.payload as Record<string, unknown>);
                  }
                  brain.dismissDraft(turn.id);
                } finally {
                  setApproving(null);
                }
              }}
              onDismiss={() => brain.dismissDraft(turn.id)}
              isDraftDrill={turn.draft ? isApplyable(turn.draft.type) : false}
            />
          ))}
          <li aria-hidden="true" style={{ height: 1 }} />
        </ul>
      )}

      <div style={promptStyle}>
        <BrainPrompt
          hint={brain.loading ? 'Brain is thinking…' : 'Tell Brain what to draft or refine'}
          placeholder="e.g. 4v2 rondo with pressing trigger"
          listening={voice.listening}
          voiceStatus={voice.status}
          onMicStart={() => void voice.start()}
          onMicStop={voice.stop}
          onSubmit={(text) => brain.send(text)}
        />
        {brain.status && <span style={statusStyle}>{brain.status}</span>}
      </div>
    </div>
  );
}

// ---- sub-components -------------------------------------------------------

interface TurnCardProps {
  turn: { id: string; role: 'user' | 'assistant'; text: string; draft?: { type: string; payload: unknown } | null; status?: 'pending' | 'approved' | 'dismissed' };
  approving: boolean;
  isDraftDrill: boolean;
  currentSpec: DrillSpec | null;
  onApprove: () => void;
  onDismiss: () => void;
}

function TurnCard({ turn, approving, isDraftDrill, onApprove, onDismiss }: TurnCardProps): React.JSX.Element {
  const isBrain = turn.role === 'assistant';
  return (
    <li style={isBrain ? brainTurnStyle : coachTurnStyle}>
      <span style={speakerStyle}>{isBrain ? 'Brain' : 'You'}</span>
      {turn.draft ? (
        <DraftCard
          type={turn.draft.type}
          payload={turn.draft.payload}
          status={turn.status}
          approving={approving}
          approveDisabled={!isDraftDrill}
          approveDisabledReason={
            !isDraftDrill
              ? 'Apply only handles drill drafts here. For tactics or other drafts use the global Brain panel.'
              : undefined
          }
          onApprove={onApprove}
          onDismiss={onDismiss}
        />
      ) : (
        <p style={turnTextStyle}>{turn.text}</p>
      )}
    </li>
  );
}

function Examples({ onPick }: { onPick: (text: string) => void }): React.JSX.Element {
  // Seeds cover the four drill-canvas setup axes: pitch sizing, roster
  // count + roles, equipment, arrows. Coach can tap one to bootstrap
  // or speak / type their own.
  const seeds = [
    'Set the pitch to a half-attacking working area',
    'Set up 4 attackers + 3 defenders + 1 coach as server',
    'Draft a 4v2 rondo with 6 cones around the grid',
    'Add 2 mannequins as the central defenders and a press arrow on the receiver',
    'Refine: tighten the grid by 5 yards and add a third bumper',
    'Make this a 3v3 SSG with mini goals at each end',
  ];
  return (
    <div style={examplesStyle}>
      <div style={examplesLabelStyle}>Try one</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {seeds.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            style={exampleBtnStyle}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- styles ---------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  ...glassPanel,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  fontFamily: 'system-ui, sans-serif',
  minWidth: 280,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: 12,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const eyebrowStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  opacity: 0.55,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 600,
};

const historyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 12,
  listStyle: 'none',
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const brainTurnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: 'flex-start',
};

const coachTurnStyle: React.CSSProperties = {
  ...brainTurnStyle,
  alignItems: 'flex-end',
};

const speakerStyle: React.CSSProperties = {
  fontSize: 10,
  opacity: 0.6,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
};

const turnTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.4,
  background: 'rgba(255,255,255,0.05)',
  padding: '8px 10px',
  borderRadius: 6,
  maxWidth: '90%',
};

const promptStyle: React.CSSProperties = {
  padding: 12,
  borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const statusStyle: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.7,
  fontStyle: 'italic',
};

const examplesStyle: React.CSSProperties = {
  flex: 1,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const examplesLabelStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  opacity: 0.55,
};

const exampleBtnStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
};

// ---- enum normalisers -----------------------------------------------------
// Brain emits free-form values ("intermediate", "Rondo", "In Possession").
// Postgres rejects anything outside its enum, so we normalise here and
// drop any value we can't map — better to omit a field than to fail
// the whole row update.

function normaliseDrillType(v: string | null): string | null {
  if (!v) return null;
  const s = v.toUpperCase().replace(/[\s-]/g, '_');
  const allowed = ['WARM_UP', 'TECHNICAL', 'TACTICAL', 'SMALL_SIDED_GAME', 'COOL_DOWN'];
  if (allowed.includes(s)) return s;
  // Common synonyms
  if (/RONDO|POSSESSION|SSG|GAME|MATCH/i.test(v)) return 'SMALL_SIDED_GAME';
  if (/WARM/i.test(v)) return 'WARM_UP';
  if (/COOL/i.test(v)) return 'COOL_DOWN';
  if (/TACTIC/i.test(v)) return 'TACTICAL';
  return 'TECHNICAL';
}

function normaliseDifficulty(v: string | null): string | null {
  if (!v) return null;
  const s = v.toUpperCase();
  if (s.startsWith('FOUND') || s.startsWith('BEGIN')) return 'FOUNDATION';
  if (s.startsWith('ADV') || s.startsWith('HARD')) return 'ADVANCED';
  return 'INTERMEDIATE';
}

function normaliseCornerId(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (s.startsWith('corner-')) {
    // Validate it's one of the known slugs.
    if (['corner-technical', 'corner-physical', 'corner-psych', 'corner-social'].includes(s)) return s;
    return null;
  }
  if (/tech|tactic/.test(s)) return 'corner-technical';
  if (/phys|fitness|speed|strength|agility/.test(s)) return 'corner-physical';
  if (/psych|mental|conf|composure|resil/.test(s)) return 'corner-psych';
  if (/social|comm|team|leader/.test(s)) return 'corner-social';
  return null;
}

function normalisePhase(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase().replace(/\s+/g, '_');
  if (['attacking', 'defending', 'transitions', 'individual'].includes(s)) return s;
  if (/possess|attack|in_poss/.test(s)) return 'attacking';
  if (/defen|out_of/.test(s)) return 'defending';
  if (/trans/.test(s)) return 'transitions';
  if (/indiv/.test(s)) return 'individual';
  return null;
}
